import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ExternalLink } from 'lucide-react';
import { SlotPicker, ReservationPicker } from '../components/BookingPickers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Embeddable booking widget — renders the SlotPicker (experience) or
 * ReservationPicker (reservation) on a stripped, iframe-friendly page
 * so partners can drop the widget on their own site (Auraria-style).
 *
 * URL shapes:
 *   /embed/ex/{event_id}    — experience booking
 *   /embed/res/{event_id}   — reservation booking
 *
 * Query params:
 *   ?accent=#hex            — recolors the primary action
 *   ?theme=light|dark       — forces a theme (default: matches parent)
 *
 * Cross-frame contract:
 *   Posts {type: 'eventism:resize', height: <px>} to window.parent on
 *   every render so the partner's iframe can auto-grow. Also posts
 *   {type: 'eventism:booked', booking_id, kind} on a successful checkout.
 */
export default function EmbedBookingPage({ kind }) {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const accent = params.get('accent');
  const themeParam = params.get('theme'); // light | dark | (null)
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  // ---- Load event ----
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/hosted-events/${eventId}`)
      .then(res => { if (alive) setEvent(res.data?.event || res.data); })
      .catch(() => { if (alive) setError('Listing not found'); });
    return () => { alive = false; };
  }, [eventId]);

  // ---- Apply theme override (forces dark/light regardless of parent) ----
  useEffect(() => {
    if (themeParam === 'light') document.documentElement.classList.remove('dark');
    if (themeParam === 'dark') document.documentElement.classList.add('dark');
  }, [themeParam]);

  // ---- Auto-resize iframe parent on every layout change ----
  // We post the body's scrollHeight whenever it changes so the partner
  // site doesn't end up with a scrollbar-in-a-scrollbar.
  useEffect(() => {
    if (!containerRef.current) return;
    const post = () => {
      const h = Math.ceil(document.body.scrollHeight);
      try {
        window.parent?.postMessage({ type: 'eventism:resize', height: h }, '*');
      } catch { /* parent on a different origin without postMessage tolerance — ignore */ }
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [event, error]);

  // Surface booking completions to the parent so partners can fire
  // their own analytics events when a sale closes.
  useEffect(() => {
    const handler = (e) => {
      // The pickers dispatch a CustomEvent('eventism:booked', {detail})
      // — bubbled here so we forward to the parent iframe.
      try {
        window.parent?.postMessage({ type: 'eventism:booked', ...e.detail }, '*');
      } catch { /* silently ignore parent postMessage rejection */ }
    };
    window.addEventListener('eventism:booked', handler);
    return () => window.removeEventListener('eventism:booked', handler);
  }, []);

  if (error) {
    return (
      <div className="min-h-[200px] flex items-center justify-center p-6 bg-background text-sm text-muted-foreground">
        {error}
      </div>
    );
  }
  if (!event) {
    return (
      <div className="min-h-[200px] flex items-center justify-center p-6 bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Mismatched kind guard — if a partner pasted the wrong embed (e.g.
  // /embed/res/ on an experience), redirect them to the right widget.
  const expectedKind = kind;
  if (event.kind !== expectedKind) {
    return (
      <div className="min-h-[200px] flex items-center justify-center p-6 bg-background text-center text-sm text-muted-foreground">
        This listing is a <strong className="mx-1">{event.kind}</strong>. Use the matching embed snippet.
      </div>
    );
  }

  const accentStyle = accent ? { '--ring': accent, accentColor: accent } : undefined;
  const canonicalUrl = `${window.location.origin}/${kind === 'experience' ? 'ex' : 'res'}/${event.city || 'denver'}/${event.slug || event.event_id}`;

  return (
    <div
      ref={containerRef}
      className="bg-background text-foreground p-4 sm:p-6"
      style={accentStyle}
      data-testid="embed-booking-root"
    >
      {/* Compact event header — partners want context above the form so
          buyers know what they're booking even without our chrome. */}
      <div className="mb-4 pb-4 border-b border-border">
        <h2 className="font-bold text-lg leading-tight" data-testid="embed-event-name">
          {kind === 'reservation' ? (event.venue_name || event.name) : event.name}
        </h2>
        {(event.short_description || event.tagline) && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {event.short_description || event.tagline}
          </p>
        )}
      </div>

      {/* The actual booking widget */}
      {kind === 'experience'
        ? <SlotPicker event={event} />
        : <ReservationPicker event={event} />}

      {/* White-label "View on Eventism" backlink — small, unobtrusive,
          but always present so partners can credit us + buyers can
          escape to the full canonical listing. Per product decision
          (1b): white-label but linked-back. */}
      <div className="mt-5 pt-3 border-t border-border/60 flex items-center justify-between">
        <a
          href={canonicalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="embed-canonical-link"
        >
          View on Eventism <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>
    </div>
  );
}
